import {
  Divider,
  Grid,
  Hidden,
  lighten,
  List,
  ListItem,
  makeStyles,
  Typography
} from '@material-ui/core';
import { MDXProvider } from '@mdx-js/react';
import { graphql } from 'gatsby';
import { getSrc } from 'gatsby-plugin-image';
import { FileNode } from 'gatsby-plugin-image/dist/src/components/hooks';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import scrollTo from 'gatsby-plugin-smoothscroll';
import React from 'react';
import Affix from '../../components/affix';
import Link from '../../components/link';
import PageLayout from '../../components/page-layout';
import { HeaderLink } from '../../components/page-layout/header';
import Section from '../../components/section';
import Seo from '../../components/seo';
import DocsSidebar from './_sidebar';

export const query = graphql`
  query ($slug: String!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    page: allMdx(filter: { fields: { slug: { eq: $slug } } }, limit: 1) {
      edges {
        node {
          body
          tableOfContents
          frontmatter {
            title
            description
            author
            date(formatString: "MM/DD/YY")
            image {
              childImageSharp {
                gatsbyImageData(width: 1000)
              }
            }
          }
        }
      }
    }
    all: allMdx(
      filter: { fileAbsolutePath: { regex: "/docs/" } }
      sort: { fields: fields___order }
    ) {
      edges {
        node {
          id
          fileAbsolutePath
          frontmatter {
            title
          }
          fields {
            order
            parent
            slug
            key
          }
        }
      }
    }
  }
`;

type TableOfContentsProps = {
  url: string;
  title: string;
  items?: Array<TableOfContentsProps>;
};

type DocPageProps = {
  data: {
    site: {
      siteMetadata: {
        siteUrl: string;
      };
    };
    page: {
      edges: Array<{
        node: {
          body: any;
          tableOfContents: {
            items: Array<TableOfContentsProps>;
          };
          frontmatter: {
            title: string;
            description: string;
            slug: string;
            date: string;
            image: FileNode;
          };
        };
      }>;
    };
    all: {
      edges: Array<{
        node: {
          body: any;
          tableOfContents: {
            items: Array<TableOfContentsProps>;
          };
          frontmatter: {
            title: string;
          };
          fields: {
            parent: string;
            key: string;
            order: string;
            slug: string;
          };
        };
      }>;
    };
  };
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#EFF3F9',
    '& .gatsby-highlight': {
      backgroundColor: theme.palette.secondary.main,
    },
    '& :not(pre) > code[class*="language-"]': {
      backgroundColor: lighten(theme.palette.secondary.light, 0.2),
      padding: theme.spacing(0.25, 1),
    },
  },

  content: {
    backgroundColor: '#ffffff',
    borderLeft: '1px solid rgba(0,0,0,.12)',
    borderRight: '1px solid rgba(0,0,0,.12)',
  },

  tableOfContentsWrapper: {
    borderLeft: '1px solid rgba(0,0,0,.12)',
    padding: theme.spacing(3),
  },

  body: {
    padding: theme.spacing(2, 4),

    '& img': {
      maxWidth: '100%',
    },

    '& a': {
      color: theme.palette.primary.main,
    },

    '& .gatsby-highlight': {
      maxHeight: '500px',
    },

    '& table': {
      borderCollapse: 'collapse',
      border: '1px solid #ddd',

      '& thead': {
        textAlign: 'left',
      },

      '& td': {
        borderTop: '1px solid #ddd',
      },

      '& td, & th': {
        padding: '10px',
      },
    },

    '& h1, & h2, & h3, & h4, & h5': {
      '& > .anchor.before': {
        position: 'absolute',
        paddingRight: 4,
        top: 0,
        left: 0,
        transform: 'translateX(-100%)',

        '& > svg': {
          visibility: 'hidden',
        },
      },

      '&:hover > .anchor.before > svg': {
        visibility: 'visible',
      },
    },
  },
}));

const useTocStyles = makeStyles(theme => ({
  tableOfContentsItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

const TableOfContents: React.FC<{ items?: Array<TableOfContentsProps> }> = props => {
  const classes = useTocStyles();

  if (!props.items?.length) {
    return null;
  }

  return (
    <List>
      {props.items.map(item => (
        <div key={item.url}>
          <ListItem className={classes.tableOfContentsItem}>
            <Link onClick={() => scrollTo(item.url)} style={{ cursor: 'pointer' }}>
              {item.title}
            </Link>
          </ListItem>
          <TableOfContents {...item} />
        </div>
      ))}
    </List>
  );
};

const DocPage: React.FC<DocPageProps> = props => {
  const classes = useStyles();
  const { title, description, image, date } = props.data.page.edges[0].node.frontmatter;
  const { body, tableOfContents } = props.data.page.edges[0].node;
  const imageSrc = getSrc(image);

  const components = {
    div: props =>
      props.className.includes('gatsby-highlight') ? (
        <div {...props} style={{ ...props.style, overflow: 'auto' }} />
      ) : (
        <div {...props} />
      ),
  };

  let links: HeaderLink[] = props.data.all.edges
    .filter(edge => !edge.node.fields.parent && edge.node.fields.key !== '_NO-PAGES-HERE')
    .map(edge => ({
      label: edge.node.frontmatter.title || edge.node.fields.key,
      children: props.data.all.edges
        .filter(child => child.node.fields.parent === edge.node.fields.key)
        .map(child => ({
          label: child.node.frontmatter.title || child.node.fields.key,
          path: `/docs/${child.node.fields.slug}/`,
        })),
    }));

  return (
    <PageLayout className={classes.root} showFooterHero={false} mobileLinks={links}>
      <Seo
        title={title}
        titleTemplate="%s | Architect Documentation"
        description={description}
        image={imageSrc}
      />

      <Divider />

      <Section maxWidth="xl" verticalSpacing={0}>
        <Grid container>
          <Hidden smDown>
            <Grid item lg={2} md={3}>
              <Affix top={0} offset={24}>
                <DocsSidebar links={links} />
              </Affix>
            </Grid>
          </Hidden>

          <Grid item lg={10} md={9} xs={12}>
            <div className={classes.content}>
              <Grid container>
                <Grid
                  item
                  xl={tableOfContents.items?.length ? 9 : false}
                  lg={tableOfContents.items?.length ? 9 : false}
                  xs={12}
                  className={classes.body}
                >
                  <MDXProvider components={components}>
                    <MDXRenderer>{body}</MDXRenderer>
                  </MDXProvider>
                </Grid>

                <Hidden mdDown>
                  <Grid item xl={3} lg={3} className={classes.tableOfContentsWrapper}>
                    <Affix top={70}>
                      <Typography variant="overline">Contents</Typography>

                      <TableOfContents items={tableOfContents.items} />
                    </Affix>
                  </Grid>
                </Hidden>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Section>
    </PageLayout>
  );
};

export default DocPage;
